import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LevelBadge } from "@/components/LevelBadge";
import {
  Target,
  Trophy,
  Wallet,
  MoreHorizontal,
  LayoutDashboard,
  ShoppingBag,
  Newspaper,
  Shield,
  MessageSquare,
  Wrench,
  LogOut,
  X,
  Users,
  Crown,
} from "lucide-react";

const PRIMARY = [
  { to: "/friends", label: "Friends", icon: Users },
  { to: "/lobby", label: "Lobby", icon: Target },
  { to: "/entries", label: "Entries", icon: Trophy },
];

export const MobileTabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  const more = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/manager", label: "Manager Hub", icon: Crown },
    { to: "/wallet", label: "Wallet", icon: Wallet },
    { to: "/blog", label: "Previews", icon: Newspaper },
    { to: "/merch", label: "Merch", icon: ShoppingBag },
    { to: "/legal", label: "Legal States", icon: Shield },
    { to: "/contact", label: "Contact", icon: MessageSquare },
    ...(user?.is_admin ? [{ to: "/admin", label: "Admin", icon: Wrench }] : []),
  ];
  const moreActive = more.some((m) => isActive(m.to));

  return (
    <>
      {sheetOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => setSheetOpen(false)}
          data-testid="mobile-more-backdrop"
        />
      )}

      <div
        className={`md:hidden fixed inset-x-0 z-50 border-t border-white/10 bg-[#0a0a0a] transition-transform duration-300 ${
          sheetOpen ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}
        data-testid="mobile-more-sheet"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <span className="text-white/40 text-xs uppercase tracking-widest">More</span>
          <button
            onClick={() => setSheetOpen(false)}
            data-testid="mobile-more-close"
            className="p-1.5 text-white/50 hover:text-white"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-2">
          {more.map((m) => {
            const Icon = m.icon;
            return (
              <Link
                key={m.to}
                to={m.to}
                data-testid={`mobile-more-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-sm text-sm uppercase tracking-widest font-semibold ${
                  isActive(m.to) ? "bg-white/10 text-white" : "text-white/70 active:bg-white/5"
                }`}
              >
                <Icon size={17} /> {m.label}
              </Link>
            );
          })}
          <button
            onClick={logout}
            data-testid="mobile-more-logout"
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-sm text-sm uppercase tracking-widest font-semibold text-[#FF3B30] active:bg-[#FF3B30]/10"
          >
            <LogOut size={17} /> Log out
          </button>
        </div>
      </div>

      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-white/10 bg-black/90 backdrop-blur-xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-tab-bar"
      >
        <div className="grid grid-cols-4 h-16">
          {PRIMARY.map((t) => {
            const Icon = t.icon;
            const active = isActive(t.to);
            return (
              <button
                key={t.to}
                onClick={() => navigate(t.to)}
                data-testid={`tab-${t.label.toLowerCase()}`}
                className={`flex flex-col items-center justify-center gap-1 transition-colors ${
                  active ? "text-[#00FF66]" : "text-white/50 active:text-white"
                }`}
              >
                <Icon size={20} />
                <span className="text-[10px] uppercase tracking-widest font-bold">{t.label}</span>
                {active && <span className="absolute top-0 h-0.5 w-10 bg-[#00FF66]" />}
              </button>
            );
          })}
          <button
            onClick={() => setSheetOpen((s) => !s)}
            data-testid="tab-more"
            className={`flex flex-col items-center justify-center gap-1 transition-colors ${
              sheetOpen || moreActive ? "text-[#00FF66]" : "text-white/50 active:text-white"
            }`}
          >
            <div className="relative">
              <MoreHorizontal size={20} />
              {user?.is_admin && (
                <span className="absolute -top-0.5 -right-1.5 w-1.5 h-1.5 rounded-full bg-[#FF9F0A]" />
              )}
            </div>
            <span className="text-[10px] uppercase tracking-widest font-bold">More</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export const MobileLevelChip = ({ level }) => (
  <div className="sm:hidden">
    <LevelBadge level={level} testId="mobile-level-badge" />
  </div>
);
